import { Component, model, input, effect, inject, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DatePicker } from 'primeng/datepicker';
import { LineItemModel } from '../../../../models/template.model';
import { SpeechService } from '../../../../services/speech.service';
import { ValidationService } from '../../../../services/validation.service';

@Component({
  selector: 'app-datepicker-control',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePicker],
  template: `
    <div class="checklist-control-row">
      <label [for]="'datepicker-' + lineItem().lineItemId" class="checklist-control-label">
        {{ lineItem().name }}@if (lineItem().isRequired) { <span class="required-indicator">*</span>}
      </label>
      <div class="checklist-control-input">
        <p-datepicker
          [inputId]="'datepicker-' + lineItem().lineItemId"
          [(ngModel)]="dateValue"
          (ngModelChange)="onDateChange($event)"
          [disabled]="disabled()"
          [invalid]="hasError()"
          [showIcon]="true"
          iconDisplay="input"
          dateFormat="yy-mm-dd"
          placeholder="Select date..."
          appendTo="body"
          [fluid]="true"
          (onBlur)="onBlur()"
        />
        @if (hasError()) {
          @for (err of errors(); track $index) {
            <small class="p-error d-block">{{ err }}</small>
          }
        } @else if (hint()) {
          <small class="text-color-secondary">{{ hint() }}</small>
        }
      </div>
      <div class="ctrl-speaker-col">
        <button type="button" class="ctrl-speaker-btn" (click)="speakLabel()"
          [attr.aria-label]="'Read label: ' + lineItem().name">
          <i class="ri-volume-up-line"></i>
        </button>
      </div>
    </div>
  `,
  styles: []
})
export class DatepickerControlComponent {
  lineItem = input.required<LineItemModel>();
  value = model<any>(null);
  disabled = input<boolean>(false);
  showError = input<boolean>(false);

  dateValue: Date | null = null;

  private speechService = inject(SpeechService);
  private validationService = inject(ValidationService);
  touched = signal(false);

  errors = computed(() => {
    if (!this.showError() && !this.touched()) return [];
    return this.validationService.validate(this.lineItem(), this.value());
  });

  hasError = computed(() => this.errors().length > 0);
  hint = computed(() => this.hasError() ? '' : this.validationService.getHint(this.lineItem()));

  private parseDatePickerValue(value: unknown): Date | null {
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === 'string') {
      const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
      }
    }

    if (typeof value === 'string' || typeof value === 'number') {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    return null;
  }

  constructor() {
    // Convert string dates to Date objects when value changes from parent
    effect(() => {
      this.dateValue = this.parseDatePickerValue(this.value());
    });
  }

  onDateChange(date: Date | null): void {
    // Sync the date change back to the value model
    this.value.set(date);
  }

  onBlur(): void {
    this.touched.set(true);
  }

  speakLabel(): void {
    this.speechService.speak(this.lineItem().name);
  }
}
